// SCR-TABLET-001 با حساب دستگاه (US-IAM-053، US-DOC-031؛ AP-IAM-05 — C-AGENT-809): تبلت با کد ۸ رقمی جفت می‌شود (اعتبارنامه فقط در کوکی
// HttpOnly) و فقط جلسه تحویلی را اجرا می‌کند که کارکن به همین تبلت سپرده است؛ بیرون از جلسه صفحه انتظار است و هیچ بخش دیگری از سامانه
// در دسترس نیست. خروج از جلسه با PIN کارکن.
import TabletAndroidOutlinedIcon from '@mui/icons-material/TabletAndroidOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DeviceTokenResponse } from '@phoenix/api-client';
import { DeviceSessionProvider, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { TabletModePage } from './TabletModePage';

export const DEVICE_PATH = '/device';

function PairForm({ pair }: { pair: (code: string) => Promise<unknown> }) {
  const { t } = useTranslation('doc');
  const [code, setCode] = useState('');
  const submit = useMutation({ mutationFn: () => pair(code) });
  return (
    <Paper sx={{ p: 4, maxWidth: 420, mx: 'auto', mt: 8 }} data-testid="device-pair">
      <Stack spacing={2} component="form" onSubmit={(event) => { event.preventDefault(); submit.mutate(); }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TabletAndroidOutlinedIcon color="primary" />
          <Typography variant="h5" component="h1">{t('doc:device.pair_title')}</Typography>
        </Stack>
        <Typography color="text.secondary">{t('doc:device.pair_hint')}</Typography>
        <TextField
          autoFocus
          label={t('doc:device.code')}
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/[^0-9۰-۹]/g, '').slice(0, 8))}
          slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', autoComplete: 'off', 'data-testid': 'device-code' } }}
        />
        {submit.error ? <ErrorState error={submit.error} compact /> : null}
        <Button type="submit" variant="contained" size="large" disabled={code.length !== 8 || submit.isPending} data-testid="device-pair-submit">
          {t('doc:device.pair')}
        </Button>
      </Stack>
    </Paper>
  );
}

function AssignedSession({ device }: { device: DeviceTokenResponse }) {
  const { t } = useTranslation('doc');
  const { tenantApi } = useTenantSession();
  const [finished, setFinished] = useState<string | null>(null);
  const assigned = useQuery({
    queryKey: ['documents', device.tenantId, 'device', device.deviceId, 'assigned'],
    queryFn: async () => {
      const result = await tenantApi.GET('/api/v1/documents/tablet-sessions/assigned');
      if (result.response.status === 204) {
        return null;
      }
      return unwrap(Promise.resolve(result));
    },
    refetchInterval: 5_000,
  });
  const session = assigned.data;
  if (session && session.id !== finished) {
    return <TabletModePage sessionId={session.id} onExited={() => { setFinished(session.id); void assigned.refetch(); }} />;
  }
  return (
    <Paper sx={{ p: 4, maxWidth: 520, mx: 'auto', mt: 8, textAlign: 'center' }} data-testid="device-waiting">
      <TabletAndroidOutlinedIcon color="primary" sx={{ fontSize: 48 }} />
      <Typography variant="h5" component="h1" gutterBottom>{device.name}</Typography>
      <Typography color="text.secondary">{t('doc:device.waiting')}</Typography>
      {assigned.error ? <ErrorState error={assigned.error} compact /> : null}
    </Paper>
  );
}

export function DeviceModePage({ apiBaseUrl, fetch }: { apiBaseUrl: string; fetch?: typeof globalThis.fetch }) {
  const { t } = useTranslation('doc');
  return (
    <Box data-screen-id="SCR-TABLET-001" sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <DeviceSessionProvider baseUrl={apiBaseUrl} fetch={fetch}>
        {({ status, device, pair, retry }) => {
          if (status === 'loading') {
            return <LoadingState rows={3} />;
          }
          if (status === 'unpaired') {
            return <PairForm pair={pair} />;
          }
          if (status === 'error' || !device) {
            return (
              <Alert severity="error" action={<Button onClick={retry}>{t('doc:device.retry')}</Button>}>
                {t('doc:device.offline')}
              </Alert>
            );
          }
          return <AssignedSession device={device} />;
        }}
      </DeviceSessionProvider>
    </Box>
  );
}
